import React from 'react';
import { debounce } from 'lodash';
import axios from 'axios';
import { EClientProduct } from '@shared/enums/EClientProduct';
import { InfoCircleTwoTone } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ERoute28Product } from '@shared/enums/ERoute28Product';
import {
    CLIENTS_API_URL,
    ECP_API_URL,
    ENTITIES_API_URL,
    FORM_API_URL,
    GLEIF_API_URL,
    LEI_DUPLICATE_ENTRY,
    ONBOARDING_URL,
} from '../../../constants/api';
import { IEcp } from './ecp';

export const apiInstance = axios.create({
    timeout: 60 * 1000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

async function fetchGleif(lei) {
    const response = await fetch(`${GLEIF_API_URL}/${lei}`);
    return response.json();
}

export async function getGleif(lei) {
    return fetchGleif(lei);
}

export async function validateDuplicateLei(lei) {
    const response = await fetch(`${LEI_DUPLICATE_ENTRY}/${lei}`, {
        credentials: 'include',
    });
    return response.json();
}

export async function getEntities() {
    const response = await fetch(`${ENTITIES_API_URL}`, {
        credentials: 'include',
    });
    return response.json();
}

export async function getEntity(clientId) {
    const response = await fetch(`${ENTITIES_API_URL}/${clientId}`, {
        credentials: 'include',
    });
    return response.json();
}

const updateForm = async (formId, { changedFields }, clientId) => {
    const validFields = changedFields.filter(({ errors }) => !errors.length);
    const fields = {};

    validFields.forEach((field) => {
        fields[field.name[0]] = field.value;
    });

    if (!validFields.length) {
        return true;
    }

    const response = await fetch(`${FORM_API_URL}/${formId}/${clientId}`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fields }),
    });

    return response.json();
};

export const updateFormDebounced = debounce(updateForm, 2000);

export const getFormData = async (formId, clientId) => {
    const response = await fetch(`${FORM_API_URL}/${formId}/${clientId}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getEcp = async (countryCode: string): Promise<IEcp[]> => {
    try {
        const ecps = await apiInstance.get(`${ECP_API_URL}/${countryCode}`);
        return ecps.data;
    } catch {
        return [];
    }
};

export const updateOnboarding = async (id, data) =>
    apiInstance.post(`${ONBOARDING_URL}/${id}`, data);

export const getPendingClients = async () =>
    apiInstance.get(`${CLIENTS_API_URL}/counterparties-pending-information`);

export const entityProducts = [
    {
        value: EClientProduct.DIGITAL_ASSETS,
        label: 'Digital Assets',
    },
    {
        value: EClientProduct.FIXED_INCOME,
        label: 'FI-Repo',
    },
    {
        value: EClientProduct.DELIVERABLE_FX,
        label: 'Deliverable FX (Spot and FWDs)',
    },
    { value: EClientProduct.PRECIOUS_METALS, label: 'Precious Metals' },
    { value: EClientProduct.FCM, label: 'FCM' },
    {
        value: EClientProduct.ROUTE28,
        label: 'Route28',
    },
    {
        value: EClientProduct.MFL,
        label: 'MFL',
    },
    {
        value: EClientProduct.SECURITIES_LENDING,
        label: 'Securities Lending',
    },
];

export const route28Products = [
    ERoute28Product.EQUITY_INDICES,
    ERoute28Product.DIGITAL,
    ERoute28Product.PRECIOUS_METALS,
    ERoute28Product.FOREIGN_EXCHANGE,
];

export const route28CollateralType = [
    {
        value: EClientProduct.DIGITAL_ASSETS,
        label: 'Digital assets (BTC, ETH, etc.)',
    },
    {
        value: EClientProduct.DELIVERABLE_FX,
        label: 'Fiat denominated assets (USD, EUR, GBP, etc.)',
    },
];
